/* eslint-disable no-param-reassign */
/* eslint-disable no-tabs */
import axios from "axios";
import { backEndBaseURL } from "../constants/settings";
import { isWeb } from "./platform";
import useStores from "../hooks/use-stores";
import { navigate } from "gatsby";
import { GetUserProfile, IsAuthed } from "./general";
import { t } from "i18next";
import { useIsDesktop } from "../hooks/use-is-desktop";
import { GetMiniUrl } from "./mini";
import { SetLocalStorage } from "./cryptography";
import { USER_INFO } from "../constants/storage-keys";

let authErrorHandled = false; // 是否已经处理过 401（只弹一次）
let authExpired = false;      // 是否已经判定 token 过期，用于后续请求直接取消

// 403/600 经常是同一批并发请求（比如 initPage 里的 Promise.all）一起失败——
// 每一个都会走到各自调用处自己的 .catch(GetErrorInfo) 弹一次 toast，这里只
// 保证同一个短时间窗口内只有第一个真正弹出来，后面的打上 suppressNotify 标记
// 让 GetErrorInfo 直接返回空字符串（不拦截/不重试原请求本身）。
let last403NotifyAt = 0;
let last600NotifyAt = 0;
const ERROR_NOTIFY_DEDUP_MS = 3000;

export const Axios = axios.create({
  validateStatus(status: number) {
    return status >= 200 && status < 400;
  },
  baseURL: backEndBaseURL,
  // timeout: 5000,
});

Axios.interceptors.request.use(
  // request 拦截器
  async (config: any) => {
    // 如果已经判定 token 过期，后续所有请求都直接取消
    if (authExpired) {
      // 用 Error("ERR_CANCELED")，方便 GetErrorInfo 统一识别静默
      return Promise.reject(new Error("ERR_CANCELED"));
    }

    // While viewing/editing a clinic that isn't the user's real
    // currentClinicId (clinic/info.tsx, clinic/products/index.tsx — see
    // UserStore.selectClinic's isTemp param), every request uses that
    // clinic's scoped, non-persisted temp token instead of the real
    // session's — never localStorage.USER_TOKEN, so the real token/session
    // is completely unaffected once the temp page is left.
    const { userStore } = useStores();
    let token = "";
    if (userStore.isTempPermissionActive && userStore.tempToken) {
      token = userStore.tempToken;
    } else {
      // if (isWeb) {
      token = localStorage.USER_TOKEN;
      // }
    }
    config.headers.authorization = token;
    config.headers["Content-Type"] = "application/json"; // To avoid OPTION request

    config.headers["ngrok-skip-browser-warning"] = "any";
    // console.log('[Network Request]', config.baseURL + config.url);
    return config;
  }, // 在发送请求时执行函数,可用来给 headers 携带 token
  (error: any) => {
    console.error("axios.request.error", error);
    throw error;
  }
);

Axios.interceptors.response.use(
  // response 拦截器：HTTP 状态码在 validateStatus 范围内（2xx/3xx）会走这里
  (response: any) => {
    if (process.env.NODE_ENV !== "production") {
      console.log(
        response.config.baseURL + response.config.url,
        "status:",
        response.status
      );
    }
    return response;
  },
  // validateStatus 返回 false（4xx/5xx）时执行
  (error) => {
    try {
      // 被主动取消的请求，统一静默
      if (error?.code === "ERR_CANCELED" || error?.message === "ERR_CANCELED") {
        return Promise.reject(new Error("ERR_CANCELED"));
      }

      const status = error.response?.status;
      const { userStore, packageStore, clinicStore, rootStore } = useStores();
      // While clinic/info.tsx / clinic/products/index.tsx are using a
      // clinic-scoped temp token (UserStore.isTempPermissionActive — see
      // UserStore.selectClinic's isTemp param), EVERY in-flight request is
      // currently riding on that token, not the real session's. A
      // 401/402/403/600 here almost certainly reflects the temp clinic (e.g.
      // its OWN package lapsed, the temp role genuinely lacks something, or
      // the viewer's relationship with THAT clinic — not their real one —
      // just ended) — none of that should force-logout the user, redirect
      // them off this page, or mutate the real session's currentClinicId/
      // availableClinics based on what's actually the temp clinic. Falling
      // back to exitTempPermissionMode + letting the calling code's own
      // .catch() show the error is enough.
      if (userStore.isTempPermissionActive && (status === 401 || status === 402 || status === 403 || status === 600)) {
        userStore.exitTempPermissionMode();
        return Promise.reject(error);
      }
      // 401 统一处理：只弹一次“认证过期”，只 navigate 一次
      if (status === 401) {
        // 给 error 打上标记，方便 GetErrorInfo 和业务层识别
        (error as any).isAuthError = true;
        // 如果已经处理过一次 401，后面的 401 直接往外抛，不再提示
        if (authErrorHandled) return Promise.reject(error);
        authErrorHandled = true;
        authExpired = true;
        console.log("Unauthorised...", error);
        if (IsAuthed()) rootStore.notify(t("AUTHENTICATION_EXPIRED"), "warning");
        userStore.isAuthed = false;
        navigate("/");

        return Promise.reject(error);
      }

      // 402：套餐过期
      if (status === 402) {
        const isMini = isWeb && window.location.pathname.startsWith("/mini");
        (error as any).isAuthError = true;
        // 如果已经处理过一次 401，后面的 401 直接往外抛，不再提示
        if (authErrorHandled) return Promise.reject(error);
        console.log("Need Payment...", error);
        // Only 401 means the token/session itself is dead — 402 just means
        // THIS request's target has no valid package (e.g. right after
        // quitting your only clinic). Setting authExpired here used to
        // black-hole every future request app-wide via the request
        // interceptor above, until something happened to call
        // ResetAuthErrorState — silently breaking unrelated polls (like
        // the "clinics related to me" search) for the rest of that page's
        // session.
        packageStore.getMemberPackagePermission().finally((() => {
          authErrorHandled = true;
        }));
        rootStore.notify(t("VIP_PACKAGE_EXPIRED"), "error");
        navigate(!isMini ? "/clinic" : GetMiniUrl("/clinic"));
      }

      // 403：权限不足 — 后台静默刷新一次最新的 RBAC 权限，修正本地缓存
      // 跟服务端不同步的情况；同时导航回诊所主页，因为当前页面大概率就是那个
      // 刚刚失去的权限所对应的页面（不拦截/不重试原请求，让调用方自己的
      // catch 照常处理报错）。
      if (status === 403) {
        clinicStore.getMyPermissions().catch(() => { });
        const isMini = isWeb && window.location.pathname.startsWith("/mini");
        navigate(!isMini ? "/clinic" : GetMiniUrl("/clinic"));
        const now = Date.now();
        if (now - last403NotifyAt < ERROR_NOTIFY_DEDUP_MS) (error as any).suppressNotify = true;
        else last403NotifyAt = now;
      }

      // 600：与当前工作诊所（userInfo.currentClinicId）的关系已不存在（被移
      // 除/已退出等）。如果名下还有其他诊所，自动切到其中一个（获取该诊所真
      // 实的 token/permissions）；不管有没有，都要把这家没有关系的诊所从
      // availableClinics 里移除。若当前正停留在 /clinic/info 页面，直接导航
      // 回诊所列表页——不拦截/不重试原请求，让调用方自己的 catch 照常处理报错。
      if (status === 600) {
        const userInfo = GetUserProfile();
        const currentClinicId = userInfo?.currentClinicId;
        if (userInfo && currentClinicId) {
          const remainingClinics = (userInfo.availableClinics ?? []).filter((c: any) => c.id != currentClinicId);
          userInfo.availableClinics = remainingClinics;
          if (remainingClinics.length > 0) {
            const nextClinicId = remainingClinics[0].id;
            userInfo.currentClinicId = nextClinicId;
            SetLocalStorage(USER_INFO, userInfo);
            userStore.selectClinic(nextClinicId).catch(() => { });
          } else {
            userInfo.currentClinicId = 0;
            SetLocalStorage(USER_INFO, userInfo);
          }
          // 暂时取消600自动跳转回诊所列表页，未来可能还需要恢复。
          // if (isWeb && window.location.pathname.startsWith("/clinic/info")) {
          //   navigate("/clinic");
          // }
        }
        const now = Date.now();
        if (now - last600NotifyAt < ERROR_NOTIFY_DEDUP_MS) (error as any).suppressNotify = true;
        else last600NotifyAt = now;
      }
    } catch {
      const errMsg =
        "Network error! Check the connection first. Then check the baseURL or Axios global request config." +
        "\n[RN] Make sure you're using ngrok or other non-localhost backends." +
        "\n[RN] For Conveyor extension, don't use https. Use http only.";
      throw new Error(errMsg);
    }

    return Promise.reject(error);
  }
);

// 可选：登录成功后可以手动重置这两个标记
export const ResetAuthErrorState = () => {
  authErrorHandled = false;
  authExpired = false;
};
